import * as React from "react";
import cx from "classnames";
import CreateInputBox from "common/components/overlayTooltip/components/createInputBox";
import PulseLoadingDot from "common/components/pulseLoadingDot";
import { FormattedMessage } from "react-intl";

const styles = require("./withSelection.scss");

interface IProps {
  isLoading: boolean;
  headerText: React.ReactNode;
  onClickNewItem: (item: string) => void;
  onClickSave: () => void;
  onClickCancel: () => void;
  createInputPlaceholder?: string;
  headerTagName?: keyof React.ReactHTML;
  disableSaveButton?: boolean;
}

interface IState {
  isScrollOnTop: boolean;
  isScrollOnBottom: boolean;
}

class WithSelection extends React.Component<IProps, IState> {
  public state: IState = {
    isScrollOnTop: true,
    isScrollOnBottom: false,
  };

  private readonly refListWrapper = React.createRef<HTMLDivElement>();

  public render() {
    const {
      children,
      headerText,
      onClickNewItem,
      onClickCancel,
      onClickSave,
      isLoading,
      createInputPlaceholder,
      disableSaveButton,
    } = this.props;
    const { isScrollOnTop, isScrollOnBottom } = this.state;

    if (isLoading) {
      return this.renderLoading();
    }
    return (
      <div className={styles.wrapper}>
        <header
          className={cx(styles.header, { [styles.onTop]: isScrollOnTop })}
        >
          {this.renderHeaderTitle()}
          <CreateInputBox
            onClickCreate={onClickNewItem}
            placeholder={createInputPlaceholder}
          />
        </header>
        <div
          ref={this.refListWrapper}
          className={styles.body}
          onScroll={this.handleScroll}
        >
          {children}
        </div>
        <footer
          className={cx(styles.footer, { [styles.onBottom]: isScrollOnBottom })}
        >
          <div className={styles.inner}>
            <button
              key={`${headerText}_cancel`}
              className={styles.cancelButton}
              onClick={onClickCancel}
            >
              <FormattedMessage id="cancel" />
            </button>
            <button
              key={`${headerText}_save`}
              className={styles.saveButton}
              onClick={onClickSave}
              disabled={disableSaveButton}
            >
              <FormattedMessage id="save2" />
            </button>
          </div>
        </footer>
      </div>
    );
  }

  public readonly scrollToBottom = () => {
    const list = this.refListWrapper.current;
    if (list) {
      list.scrollTo(0, list.scrollHeight);
    }
  };

  private readonly renderHeaderTitle = () => {
    const { headerTagName, headerText } = this.props;
    return React.createElement(headerTagName || "h3", {
      className: styles.title,
      children: headerText,
    });
  };

  private readonly renderLoading = () => {
    const { headerText } = this.props;
    return (
      <div className={styles.wrapper}>
        <div className={cx(styles.header, styles.loading)}>
          <div className={styles.title}>{headerText}</div>
        </div>
        <div className={styles.loadingBody}>
          <PulseLoadingDot size={8} />
        </div>
      </div>
    );
  };

  private readonly setScrollOnTop = (state: boolean) => {
    this.setState({
      isScrollOnTop: state,
    });
  };
  private readonly setScrollOnBottom = (state: boolean) => {
    this.setState({
      isScrollOnBottom: state,
    });
  };

  private readonly handleScroll: React.UIEventHandler<HTMLDivElement> = e => {
    const { isScrollOnBottom, isScrollOnTop } = this.state;
    const target = e.currentTarget;

    const stateOfScrollIsTop = target.scrollTop === 0;
    const stateOfScrollIsBottom =
      target.scrollHeight - target.scrollTop === target.offsetHeight;

    if (isScrollOnTop !== stateOfScrollIsTop) {
      this.setScrollOnTop(stateOfScrollIsTop);
    }
    if (isScrollOnBottom !== stateOfScrollIsBottom) {
      this.setScrollOnBottom(stateOfScrollIsBottom);
    }
  };
}

export default WithSelection;
